import type { ApolloClient } from '@apollo/client'

import type { GuestTag } from '~publish/helpers/guestTags'
import { CreateTag, GetTags } from '~publish/hooks/useTags'
import type { Tag } from '~publish/hooks/useTags'

export class GuestTagImportError extends Error {}

function toSentence(message: string): string {
  const trimmed = message.trim()
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`
}

export function createGuestTagResolver(
  client: ApolloClient,
  organizationId: string,
  signal: AbortSignal,
): {
  resolve: (
    tags: readonly GuestTag[] | undefined,
  ) => Promise<string[] | undefined>
  refresh: () => Promise<void>
  getLimitedTagNames: () => string[]
} {
  let accountTags: readonly Tag[] | undefined
  const resolved = new Map<string, Tag>()
  const failures = new Map<string, GuestTagImportError>()
  const limitedTagNames = new Map<string, string>()

  const refresh = async (): Promise<void> => {
    signal.throwIfAborted()
    const { data } = await client.query({
      query: GetTags,
      variables: { input: { organizationId } },
      fetchPolicy: 'network-only',
      context: {
        queryDeduplication: false,
        fetchOptions: { signal },
      },
    })
    signal.throwIfAborted()
    if (!data)
      throw new GuestTagImportError(
        'Your account tags could not be loaded. Your browser drafts are still saved.',
      )
    accountTags = data.tags
  }

  const findTag = (name: string): Tag | undefined =>
    accountTags?.find((tag) => tag.name === name) ??
    accountTags?.find((tag) => tag.name.toLowerCase() === name.toLowerCase())

  const createTag = async (
    guestTag: GuestTag,
    key: string,
  ): Promise<Tag | undefined> => {
    try {
      const { data } = await client.mutate({
        mutation: CreateTag,
        variables: { input: { organizationId, tag: guestTag } },
        context: { fetchOptions: { signal } },
      })
      signal.throwIfAborted()
      const payload = data?.createTag
      if (payload?.__typename === 'TagActionSuccess') {
        accountTags = [...(accountTags ?? []), payload.tag]
        return payload.tag
      }
      if (payload?.__typename !== 'LimitReachedError') {
        throw new Error(
          payload && 'message' in payload
            ? payload.message
            : 'The tag could not be created.',
        )
      }
    } catch (error) {
      signal.throwIfAborted()
      const failure = new GuestTagImportError(
        `Could not import tag “${guestTag.name}”. ${error instanceof Error ? toSentence(error.message) : 'Try again.'} Your browser draft is still saved.`,
      )
      failures.set(key, failure)
      await refresh()
      const tag = findTag(guestTag.name)
      if (!tag) throw failure
      failures.delete(key)
      return tag
    }
    await refresh()
    return findTag(guestTag.name)
  }

  const resolveTag = async (
    guestTag: GuestTag,
  ): Promise<string | undefined> => {
    signal.throwIfAborted()
    const key = guestTag.name.toLowerCase()
    const failed = failures.get(key)
    if (failed) throw failed
    if (limitedTagNames.has(key)) return undefined
    const tag =
      resolved.get(key) ??
      findTag(guestTag.name) ??
      (await createTag(guestTag, key))
    if (!tag) {
      limitedTagNames.set(key, guestTag.name)
      return undefined
    }
    if (tag.isLocked) {
      throw new GuestTagImportError(
        `Tag “${tag.name}” is locked in this account. Your browser draft is still saved.`,
      )
    }
    resolved.set(key, tag)
    return tag.id
  }

  const resolve = async (
    guestTags: readonly GuestTag[] | undefined,
  ): Promise<string[] | undefined> => {
    if (guestTags === undefined) return undefined
    if (guestTags.length === 0) return []
    if (!accountTags) await refresh()
    const ids = await guestTags.reduce<Promise<string[]>>(
      async (previous, tag) => {
        const resolvedIds = await previous
        const id = await resolveTag(tag)
        return id === undefined ? resolvedIds : [...resolvedIds, id]
      },
      Promise.resolve([]),
    )
    return Array.from(new Set(ids))
  }

  const getLimitedTagNames = (): string[] =>
    Array.from(limitedTagNames.values())

  return { resolve, refresh, getLimitedTagNames }
}
